import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, Share2, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import type { BlogPostItem } from '../api/types';

// Simple placeholder fetch using same dataset as Blog
import type { } from './Blog';

const PLACEHOLDER_POSTS: (BlogPostItem & { content?: string; published?: boolean })[] = [
  {
    id: '1',
    title: 'Getting Started with Bible Journaling',
    content: `# Welcome\n\nStart with a simple passage and reflect.\n\n- Choose a verse\n- Pray\n- Create intentionally\n\n> Grace upon grace.`,
    category: 'tutorials',
    createdDate: new Date().toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=1600&h=800&fit=crop',
    published: true,
  },
  {
    id: '2',
    title: 'Finding Inspiration in the Psalms',
    content: `## Inspiration\n\nThe Psalms invite honest expression — let your journaling reflect that.`,
    category: 'inspiration',
    createdDate: new Date(Date.now() - 86400000 * 7).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1513519245088-0e12902e35a6?w=1600&h=800&fit=crop',
    published: true,
  },
];

const BlogPost: React.FC = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const postId = urlParams.get('id');
  const post = PLACEHOLDER_POSTS.find(p => p.id === postId);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-[#3D3D3D] mb-4">Post not found</h2>
          <Link to="/blog" className="inline-block bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white rounded-full px-6 py-3 font-sans text-sm">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      {/* Hero Image */}
      <div className="relative h-[40vh] sm:h-[50vh] overflow-hidden">
        <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#3D3D3D]/60 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10">
        <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="p-8 sm:p-12">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              {post.category && (
                <span className="inline-flex items-center gap-2 bg-[#D4A5A5]/20 text-[#D4A5A5] px-3 py-1 rounded-full font-sans text-sm font-medium capitalize">
                  <Tag className="w-3 h-3" />
                  {post.category.replace('-', ' ')}
                </span>
              )}
              <span className="flex items-center gap-2 text-[#3D3D3D]/50 font-sans text-sm">
                <Clock className="w-4 h-4" />
                {post.createdDate && new Date(post.createdDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#3D3D3D] mb-8 leading-tight">{post.title}</h1>

            {/* Content */}
            <div className="prose prose-lg max-w-none font-sans text-[#3D3D3D]/80">
              {(post.content || '').split('\n').map((p, i) => (
                <p key={i} className="mb-4 leading-relaxed">{p}</p>
              ))}
            </div>

            {/* Share */}
            <div className="border-t border-gray-100 mt-12 pt-8 flex items-center justify-between">
              <Link to="/blog" className="inline-flex items-center gap-2 text-[#3D3D3D]/70 hover:text-[#D4A5A5] font-sans transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
              <div className="flex items-center gap-3">
                <button className="rounded-full border border-gray-200 w-9 h-9 flex items-center justify-center" aria-label="Like post" title="Like post">
                  <Heart className="w-4 h-4" />
                </button>
                <button className="rounded-full border border-gray-200 w-9 h-9 flex items-center justify-center" aria-label="Share post" title="Share post">
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.article>
      </div>
    </div>
  );
};

export default BlogPost;